import React, { useEffect, useState } from "react";
import {
  Input,
  Table,
  Space,
  Typography,
  Tooltip,
  Upload,
  message,
  Select,
  Modal,
} from "antd";
import { Row, Col, Button, Form } from "react-bootstrap";
import { BsStar, BsStarFill, BsImage, BsCameraVideo } from "react-icons/bs";
import axios from "axios";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchExercises,
  fetchSassToken,
  filterExercises,
  updateExerciseFavorite,
  updatePagination,
  upsertExercise,
} from "../../redux/Slices/toolboxExerciseSlice";
import Loader from "../../components/Common/Loader";
import ToastNotification from "../../components/Common/ToastNotification";
import { resetToastConfig } from "../../redux/Slices/toolboxExerciseSlice";
import FormLabel from "../../components/Common/FormLabel";
import { DeleteOutline } from "@mui/icons-material";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import {
  fetchBodyParts,
  fetchEquipment,
  fetchMuscleGroups,
} from "../../redux/Slices/toolboxExerciseSlice";
import FormSelect from "../../components/Common/FormSelect";

const { Text } = Typography;
const { Option } = Select;

const Exercises = () => {
  const { trans } = useCustomTranslation();
  const dispatch = useDispatch();
  useFetchCoachProfile();

  const { profileData } = useSelector((state) => state.profile);

  const { exerciseList, totalCount, loading, pagination } = useSelector(
    (state) => state.exercises
  );
  const { toastConfig } = useSelector((state) => state.exercises);
  const { bodyParts, equipment, muscleGroups } = useSelector(
    (state) => state.exercises
  );

  const coachId = profileData?.coachId;

  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadText, setUploadText] = useState("Preparing upload...");
  const [errors, setErrors] = useState({
    name: "",
    photo: "",
    video: "",
  });
  const [formData, setFormData] = useState({
    exerciseId: null,
    coachId: coachId,
    name: "",
    note: "",
    photo: null,
    video: null,
    hasImage: false,
    hasVideo: false,
    photoName: "",
    videoName: "",
    blobUrl: "",
    videoUrl: "",
    videoFile: "",
    bodyParts: "",
    equipment: "",
    muscleGroups: "",
    secondaryMuscleGroup: [],
    instructions: "",
    customized: true,
  });

  const options = [];
  for (let i = 10; i < 36; i++) {
    options.push({
      label: i.toString(36) + i,
      value: i.toString(36) + i,
    });
  }

  useEffect(() => {
    const mappedData = exerciseList.map((item) => {
      // find matching bodyPart object by name
      const bp = bodyParts.find(
        (bp) => bp?.name?.toLowerCase() === item?.bodyParts?.toLowerCase()
      );

      return {
        exerciseId: item.exerciseId || null,
        coachId: coachId, // from your props/state
        name: item.name || "",
        note: item.note || "",
        photo: item.hasImage ? item.blobUrl : null,
        video: item.hasVideo ? item.videoURL : null,
        hasImage: item.hasImage || false,
        hasVideo: item.hasVideo || false,
        photoName: item.photoName || "",
        videoName: item.videoName || "",
        blobUrl: item.blobUrl || "",
        videoUrl: item.videoURL || "",
        bodyParts: bp ? bp.id : "",
        equipment: item.equipment || "",
        muscleGroups: item.muscleGroups || "",
        secondaryMuscleGroup: item.secondaryMuscleId || [],
        instructions: item.instructions || "",
        customized: item.customized === "Yes" ? true : false,
      };
    });

    setFormData(mappedData);
  }, [exerciseList, coachId, bodyParts]);

  useEffect(() => {
    if (coachId) {
      dispatch(
        fetchExercises({
          coachId,
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, coachId]);

  useEffect(() => {
    dispatch(fetchBodyParts());
    dispatch(fetchEquipment(""));
    dispatch(fetchMuscleGroups(""));
  }, [dispatch]);

  useEffect(() => {
    if (toastConfig) {
      const timer = setTimeout(() => {
        dispatch(resetToastConfig());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastConfig, dispatch]);

  //Debounced Search
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchText.trim() === "") {
        dispatch(
          fetchExercises({
            coachId,
            pageNumber: pagination.current,
            pageSize: pagination.pageSize,
          })
        );
      } else {
        dispatch(filterExercises({ coachId, name: searchText }));
      }
    }, 300);

    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, dispatch, coachId]);

  // Handle Search Input
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const convertImageToBase64 = async (imageUrl) => {
    if (!imageUrl) return "";

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return "";
    }
  };

  const handleTableChange = (paginationConfig) => {
    dispatch(
      updatePagination({
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      })
    );
    dispatch(
      fetchExercises({
        coachId,
        pageNumber: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      })
    );
  };

  const toggleFavorite = (exerciseId, isFavourite) => {
    dispatch(updateExerciseFavorite({ exerciseId, isFavourite: !isFavourite }));
  };

  const getShowingText = () => {
    const start = (pagination.current - 1) * pagination.pageSize + 1;
    const end = Math.min(pagination.current * pagination.pageSize, totalCount);

    return totalCount
      ? `${trans("Common.showing")} ${start} ${trans(
          "Common.to"
        )} ${end} ${trans("Common.of")} ${totalCount} ${trans(
          "Common.results"
        )}`
      : trans("Common.noResults");
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setErrors({});
    setFormData({
      coachId: coachId,
      name: "",
      note: "",
      photo: null,
      video: null,
      customized: true,
      hasImage: false,
      hasVideo: false,
      photoName: "",
      videoName: "",
      blobUrl: "",
      videoUrl: "",
      bodyParts: "",
      equipment: "",
      muscleGroups: "",
      secondaryMuscleGroup: [],
      instructions: "",
    });
    setShowModal(true);
  };

  const handleEdit = (record) => {
    const isStandardExercise = record.customized === "No";
    setShowModal(true);
    setIsEditMode(!isStandardExercise);
    setErrors({});

    setFormData({
      exerciseId: record.exerciseId,
      coachId: record.coachId,
      name: record.name,
      note: record.note,
      favorite: record.favorite,
      blobUrl: record.blobUrl || "",
      videoUrl: record.videoURL || "",
      hasImage: record.hasImage,
      hasVideo: record.hasVideo,
      photoName: record.photoName || "",
      videoName: record.videoName || "",
      isCustomized: record.customized || true,
      bodyParts:
        bodyParts.find(
          (item) => item.name?.toLowerCase() === record?.bodyParts?.toLowerCase()
        )?.id || "",
      equipment:
        equipment.find(
          (item) => item.name?.toLowerCase() === record?.equipment?.toLowerCase()
        )?.id || "",
      muscleGroups:
        muscleGroups.find(
          (item) =>
            item.name?.toLowerCase() === record?.muscleGroups?.toLowerCase()
        )?.id || "",
      secondaryMuscleGroup: record.secondaryMuscleGroups
        ? record.secondaryMuscleGroups

            .map(
              (muscle) =>
                muscleGroups.find(
                  (item) => item.name?.toLowerCase() === muscle?.toLowerCase()
                )?.id
            )
            .filter((id) => id !== undefined)
        : [],
      instructions: record.instructions || "",
    });

    if (isStandardExercise) {
      setFormData((prev) => ({
        ...prev,
        disabledFields: true,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        disabledFields: false,
      }));
    }
  };

  // Handle Form Field Changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      if (!/^[A-Za-z\s]*$/.test(value)) {
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (info, type) => {
    const { file } = info;
    if (!file || !file.originFileObj) return;

    const selectedFile = file.originFileObj;
    const fileSizeMB = selectedFile.size / (1024 * 1024);

    let newErrors = { ...errors };

    if (type === "photo") {
      if (!selectedFile.type.startsWith("image/")) {
        newErrors.photo = "Invalid file type. Please upload an image.";
      } else if (fileSizeMB > 10) {
        newErrors.photo = "Image size exceeds 10MB.";
      } else {
        newErrors.photo = "";
      }
    }

    if (type === "video") {
      if (!selectedFile.type.startsWith("video/")) {
        newErrors.video = "Invalid file type. Please upload a video.";
      } else if (fileSizeMB > 500) {
        newErrors.video = "Video size exceeds 500MB.";
      } else {
        newErrors.video = "";
      }
    }

    setErrors(newErrors);

    if (newErrors.photo || newErrors.video) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      let base64Data = reader.result.split(",")[1];
      setFormData((prev) => ({
        ...prev,
        [type === "photo" ? "exerciseImageBase64" : "exerciseVideoBase64"]:
          base64Data,
        [type === "photo" ? "photoName" : "videoName"]: selectedFile.name,
        [type === "photo" ? "exerciseImageUrl" : "exerciseVideoUrl"]: "",
        // Store the actual file object for video uploads
        ...(type === "video" && { videoFile: selectedFile }),
      }));
    };
    reader.readAsDataURL(selectedFile);
  };

  // Add this utility function at the top of your component
  const sanitizeFilename = (filename) => {
    if (typeof filename !== "string") return `exercise-video-${Date.now()}.mp4`;

    const sanitized = filename
      .replace(/\s+/g, "-") // Replace spaces with dashes
      .replace(/[^a-zA-Z0-9._-]/g, "") // Remove unsafe characters
      ?.toLowerCase();

    const timestamp = Date.now(); // Milliseconds
    const extIndex = sanitized.lastIndexOf(".");

    if (extIndex !== -1) {
      // Insert timestamp before file extension
      const name = sanitized.substring(0, extIndex);
      const ext = sanitized.substring(extIndex);
      return `${name}-${timestamp}${ext}`;
    }

    // If no extension, just add timestamp
    return `${sanitized}-${timestamp}`;
  };

  // Chunked upload function for large files
  const uploadFileInChunks = async (sasUrl, file, onProgress) => {
    const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const blockIds = [];

    try {
      setUploadText(`Uploading video... (0 of ${totalChunks} chunks)`);
      // Start from 0%
      setUploadProgress(0);
      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        // Generate a block ID (must be base64 encoded and same length for all blocks)
        const blockId = btoa(String(i).padStart(6, "0"));
        blockIds.push(blockId);

        // Upload chunk
        const chunkUrl = `${sasUrl}&comp=block&blockid=${encodeURIComponent(
          blockId
        )}`;

        setUploadText(`Uploading video... (${i + 1} of ${totalChunks} chunks)`);

        await axios.put(chunkUrl, chunk, {
          headers: {
            "Content-Type": "application/octet-stream",
          },
          transformRequest: [
            function (data, headers) {
              delete headers["Authorization"];
              delete headers["authorization"];
              return data;
            },
          ],
        });

        // Calculate progress: 0-90% for chunks, reserve 10% for commit
        const progress = Math.round(((i + 1) / totalChunks) * 90);
        setUploadProgress(progress);
        if (onProgress) {
          onProgress(progress);
        }
      }

      setUploadText("Finalizing upload...");
      setUploadProgress(95);

      // Commit the blocks
      const blockListXml = `<?xml version="1.0" encoding="utf-8"?>
      <BlockList>
        ${blockIds.map((id) => `<Latest>${id}</Latest>`).join("")}
      </BlockList>`;

      const commitUrl = `${sasUrl}&comp=blocklist`;

      await axios.put(commitUrl, blockListXml, {
        headers: {
          "Content-Type": "application/xml",
          "x-ms-blob-content-type": file.type || "video/mp4",
        },
        transformRequest: [
          function (data, headers) {
            delete headers["Authorization"];
            delete headers["authorization"];
            return data;
          },
        ],
      });

      setUploadProgress(100);
      setUploadText("Upload completed successfully!");

      return { status: 201 }; // Success
    } catch (error) {
      // console.error("Chunked upload failed:", error);
      setUploadText("Upload failed. Please try again.");
      throw error;
    }
  };
  const handleSave = async () => {
    let newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }

    if (!formData.bodyParts) {
      newErrors.bodyParts = "Body Part is required.";
    }
    if (!formData.equipment) {
      newErrors.equipment = "Equipment is required.";
    }
    if (!formData.muscleGroups) {
      newErrors.muscleGroups = "Muscle Group is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    setUploadText("Preparing upload...");

    let imageBase64 = formData.exerciseImageBase64 || "";
    let videoFileName = formData.videoName || "";
    let videoUrl = formData.existingVideoUrl || "";

    // Convert image to base64 if needed
    if (!imageBase64 && formData.blobUrl) {
      setUploadText("Processing image...");
      imageBase64 = await convertImageToBase64(formData.blobUrl);
    }

    // Handle video upload first if there's a new video
    if (formData.videoFile || formData.videoUrl) {
      try {
        setUploadText("Getting upload token...");
        setUploadProgress(0);

        // Determine the filename for the video
        const videoFilename =
          sanitizeFilename(formData.videoName) ||
          (formData.videoFile
            ? sanitizeFilename(formData.videoFile.name)
            : "exercise-video.mp4");

        // Get SAS token for this filename
        const sasResponse = await dispatch(
          fetchSassToken({ fileName: videoFilename })
        ).unwrap();

        if (!sasResponse.uploadUrl) {
          throw new Error("No SAS URL returned from server");
        }

        const sasUrl = sasResponse.uploadUrl;
        setUploadProgress(10);

        // Prepare video file for upload
        let videoFile;
        if (formData.videoFile) {
          videoFile = formData.videoFile; // keep the actual File
        } else if (formData.videoUrl) {
          setUploadText("Preparing video file...");
          const response = await fetch(formData.videoUrl);
          const blob = await response.blob();
          videoFile = new File([blob], videoFilename, {
            type: blob.type || "video/mp4",
          });
        }

        // Determine upload method based on file size
        const fileSizeMB = videoFile.size / (1024 * 1024);
        const USE_CHUNKED_UPLOAD_THRESHOLD = 10; // Use chunked upload for files > 10MB

        let blobUpload;

        if (fileSizeMB > USE_CHUNKED_UPLOAD_THRESHOLD) {
          console.log(
            `File size: ${fileSizeMB.toFixed(2)}MB - Using chunked upload`
          );

          // Use chunked upload for large files
          blobUpload = await uploadFileInChunks(
            sasUrl,
            videoFile,
            (progress) => {
              console.log(`Upload progress: ${progress}%`);
            }
          );
        } else {
          // console.log(
          //   `File size: ${fileSizeMB.toFixed(2)}MB - Using single upload`
          // );

          setUploadText("Uploading video...");

          // Use single upload for smaller files
          blobUpload = await axios.put(sasUrl, videoFile, {
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": videoFile.type || "video/mp4",
            },
            transformRequest: [
              function (data, headers) {
                delete headers["Authorization"];
                delete headers["authorization"];
                return data;
              },
            ],
            onUploadProgress: (progressEvent) => {
              if (progressEvent.lengthComputable) {
                const percent = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setUploadProgress(Math.min(percent, 95)); // Cap at 95% for single upload
                // console.log(`Upload progress: ${percent}%`);
              }
            },
          });

          setUploadProgress(100);
          setUploadText("Upload completed!");
        }

        if (blobUpload.status !== 201) {
          throw new Error(
            `Video upload failed with status: ${blobUpload.status}`
          );
        }

        const blobUrl = sasUrl.split("?")[0];
        videoUrl = blobUrl;
        videoFileName = videoFilename;
      } catch (error) {
        // console.error("Failed to upload video:", error);
        // message.error("Failed to upload video. Please try again.");
        setIsSubmitting(false);
        setUploadProgress(0);
        setUploadText("Upload failed");
        return;
      }
    }

    // Continue with saving exercise data
    setUploadText("Saving exercise data...");
    setUploadProgress(100);

    // Prepare payload after video upload is complete
    const payload = {
      exerciseId: formData.exerciseId || null,
      coachId: formData.coachId,
      exerciseName: formData.name,
      note: formData.note || "",
      targetMuscleId: formData.muscleGroups,
      bodyPartsId: formData.bodyParts,
      equipmentId: formData.equipment,
      secondaryMuscleId: (formData.secondaryMuscleGroup || []).map(String),
      instructions: formData.instructions,
      isCustomized: formData.customized || true,
    };

    if (formData.photoName && imageBase64) {
      payload.exerciseImageBase64 = imageBase64;
      payload.exerciseImageFileName = formData.photoName;
      payload.exerciseImageMediaType = "image";
      payload.exerciseImageFileType = "image/jpeg";
      payload.exerciseImageSourceType = "exercise image";
    } else {
      payload.exerciseImageBase64 = "";
      payload.exerciseImageFileName = formData.photoName || "";
      payload.exerciseImageSourceType = "exercise image";
    }

    // Add video information to payload
    if (videoUrl) {
      payload.blobStorageVideoUrl = videoUrl;
      payload.exerciseVideoBase64 = "";
      payload.exerciseVideoFileName = videoFileName.replace(/\s+/g, "-");
      payload.exerciseVideoMediaType = "video";
      payload.exerciseVideoFileType = "video/mp4";
      payload.exerciseVideoSourceType = "exercise video";
    } else {
      payload.exerciseVideoBase64 = formData.exerciseVideoBase64 || "";
      payload.exerciseVideoFileName = formData.videoName || "";
      payload.exerciseVideoSourceType = formData.videoName
        ? "exercise video"
        : "";
      payload.exerciseVideoUrl = formData.existingVideoUrl || "";
    }

    try {
      await dispatch(
        upsertExercise({ coachId, exerciseData: payload })
      ).unwrap();
      dispatch(
        fetchExercises({
          coachId,
          pageNumber: 1,
          pageSize: pagination.pageSize,
        })
      );
      setShowModal(false);
      setErrors({});
      message.success("Exercise saved successfully!");
    } catch (error) {
      // console.error("Failed to save exercise:", error);
      message.error("Failed to save exercise.");
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
      setUploadText("Processing...");
    }
  };

  const handleDeleteFile = (type) => {
    if (type === "photo") {
      setFormData((prev) => ({
        ...prev,
        exerciseImageBase64: "",
        photoName: "",
        exerciseImageUrl: "",
        blobUrl: "",
        exerciseImageMediaType: "",
        exerciseImageFileType: "",
        exerciseImageSourceType: "",
      }));
    } else if (type === "video") {
      setFormData((prev) => ({
        ...prev,
        exerciseVideoBase64: "",
        videoName: "",
        exerciseVideoUrl: "",
        videoUrl: "",
        exerciseVideoMediaType: "",
        exerciseVideoFileType: "",
        exerciseVideoSourceType: "",
      }));
    }
  };

  const columns = [
    {
      title: trans("Exercise.Name"),
      dataIndex: "name",
      key: "name",
      className: "exercise-name-column",
      render: (text, record) => (
        <div className="exercise-name-wrapper">
          <span>{text}</span>
          {record.hasImage && (
            <BsImage
              className="exercise-icon image-icon"
              onClick={(e) => {
                e.stopPropagation();
                const newWindow = window.open("", "_blank");
                newWindow.document.write(
                  `<html><body><img src="${record.blobUrl}" style="max-width:100%; height:auto;" /></body></html>`
                );
                newWindow.document.close();
              }}
            />
          )}
          {record.hasVideo && (
            <BsCameraVideo
              className="exercise-icon video-icon"
              onClick={(e) => {
                e.stopPropagation();
                const newWindow = window.open("", "_blank");
                newWindow.document.write(
                  `<html><body><video controls autoplay style="max-width:100%; height:auto;"><source src="${record.videoURL}" type="video/mp4"></video></body></html>`
                );
                newWindow.document.close();
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: trans("Exercise.Favorite"),
      dataIndex: "favorite",
      key: "favorite",
      className: "favorite-column",
      render: (favorite, record) => (
        <span
          className="favorite-icon"
          onClick={() => toggleFavorite(record.exerciseId, record.favorite)}
        >
          {favorite ? <BsStarFill className="yellow-star" /> : <BsStar />}
        </span>
      ),
    },
    {
      title: trans("Exercise.Customized"),
      dataIndex: "customized",
      key: "customized",
      className: "customized-column",
    },
    {
      title: trans("Common.action"),
      key: "action",
      className: "action-column",
      render: (_, record) => (
        <Space size="small">
          <Tooltip
            title={
              record.customized === "Yes"
                ? trans("Common.edit")
                : trans("Common.view")
            }
          >
            <Button
              type="button"
              className="action-buttons"
              onClick={() => handleEdit(record)}
            >
              {record.customized === "Yes" ? (
                <i className="bi bi-pencil"></i>
              ) : (
                <i className="bi bi-eye"></i>
              )}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isSubmitting) {
    return (
      <Loader text={uploadText} progress={uploadProgress} showProgress={true} />
    );
  }

  return (
    <div className="exercise-container table-container">
      {/* Header */}
      <Row className="pb-2">
        <Col>
          <h5 className="pb-2">
            {trans("Exercise.Exercises") + " " + trans("Common.details")}
          </h5>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button className="add-button" onClick={handleAdd}>
            <i className="bi bi-plus"></i>{" "}
            {trans("Common.create") + " " + trans("Common.new")}
          </Button>
        </Col>
      </Row>

      {/* Search Bar */}
      <Row className="mb-2">
        <Col xs={12} md={6}>
          <Input
            value={searchText}
            onChange={handleSearchChange}
            placeholder={trans("Common.searchByName")}
            allowClear
            className="search-box"
          />
        </Col>
      </Row>

      {/* Table & Pagination Container */}
      <div className="">
        <Table
          className="custom-table"
          columns={columns}
          dataSource={exerciseList}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "25", "50"],
            position: ["bottomRight"],
            showTotal: () => <Text>{getShowingText()}</Text>,
          }}
          onChange={handleTableChange}
          rowKey={(record) => record.exerciseId}
          bordered={false}
          components={{
            table: (props) => (
              <table
                {...props}
                style={{
                  ...props.style,
                  borderCollapse: "separate",
                  borderSpacing: "0 10px",
                }}
              />
            ),
            header: {
              cell: (props) => {
                return (
                  <th
                    {...props}
                    style={{
                      ...props.style,
                      background: "transparent",
                      borderColor: "#FCFBFFCC",
                      fontFamily: "Proxima-Nova-Regular",
                      borderStyle: "solid",
                      borderWidth: "8px 0px",
                      padding: "12px 16px",
                      borderRadius:
                        props.children === columns[0].title
                          ? "12px 0 0 12px"
                          : props.children === columns[columns.length - 1].title
                          ? "0 12px 12px 0"
                          : "0",
                    }}
                  />
                );
              },
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    fontFamily: "Proxima-Nova-Regular",
                    background: "transparent",
                  }}
                />
              ),
            },
            body: {
              cell: (props) => (
                <td
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    borderColor: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    borderStyle: "solid",
                    borderWidth: "8px 0px",
                    padding: "12px 16px",
                    // Border radius for body cells
                    borderRadius:
                      props.index % columns.length === 0
                        ? "12px 0 0 12px"
                        : props.index % columns.length === columns.length - 1
                        ? "0 12px 12px 0"
                        : "0",
                  }}
                />
              ),
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    margin: "10px 0",
                  }}
                />
              ),
            },
          }}
        />
      </div>
      {/* Add/Edit Modal */}
      <Modal
        title={isEditMode ? trans("Exercise.Update") : trans("Exercise.Add")}
        open={showModal}
        width={700}
        onCancel={() => setShowModal(false)}
        footer={
          formData.disabledFields
            ? [
                <Button key="close" onClick={() => setShowModal(false)}>
                  {trans("Common.close")}
                </Button>,
              ]
            : [
                <Button
                  key="cancel"
                  className="cancel-button exer-save-update-button"
                  onClick={() => setShowModal(false)}
                >
                  {trans("Common.cancel")}
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  loading={isSubmitting}
                  onClick={handleSave}
                  className="ml-2 create-button exer-save-update-button"
                >
                  {isEditMode ? trans("Common.update") : trans("Common.save")}
                </Button>,
              ]
        }
      >
        <Form className="exercise-form">
          <Form.Group controlId="name">
            <FormLabel label={trans("Exercise.Name")} required />
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleFieldChange}
              className={errors.name ? "is-invalid" : ""}
              disabled={formData.disabledFields}
              required
            />
            {errors.name && (
              <div className="invalid-feedback">{errors.name}</div>
            )}
          </Form.Group>

          <Form.Group controlId="note">
            <Form.Label>{trans("Exercise.Note")}</Form.Label>
            <Input.TextArea
              name="note"
              value={formData.note}
              onChange={handleFieldChange}
              disabled={formData.disabledFields}
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group controlId="bodyParts">
                <FormSelect
                  label="Body Parts"
                  name="bodyParts"
                  value={formData.bodyParts}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      bodyParts: e.target.value, // no need for Number() since react-select handles types
                    }))
                  }
                  options={[
                    { value: "", label: "Please select" },
                    ...(bodyParts?.map((item) => ({
                      value: item.id,
                      label: item.name,
                    })) || []),
                  ]}
                  isInvalid={!!errors.bodyParts}
                  feedback={errors.bodyParts}
                  isDisabled={formData.disabledFields}
                  labelRequired
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="equipment">
                <FormSelect
                  label="Equipment"
                  name="equipment"
                  value={formData.equipment}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      equipment: e.target.value,
                    }))
                  }
                  options={[
                    { value: "", label: "Please select" },
                    ...(equipment?.map((item) => ({
                      value: item.id,
                      label: item.name,
                    })) || []),
                  ]}
                  isInvalid={!!errors.equipment}
                  feedback={errors.equipment}
                  isDisabled={formData.disabledFields}
                  labelRequired
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <Form.Group controlId="muscleGroups">
                <FormSelect
                  label="Muscle Groups"
                  name="muscleGroups"
                  value={formData.muscleGroups}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      muscleGroups: e.target.value,
                    }))
                  }
                  options={[
                    { value: "", label: "Please select" },
                    ...(muscleGroups?.map((item) => ({
                      value: item.id,
                      label: item.name,
                    })) || []),
                  ]}
                  isInvalid={!!errors.muscleGroups}
                  feedback={errors.muscleGroups}
                  isDisabled={formData.disabledFields}
                  labelRequired
                />
              </Form.Group>
            </Col>

            <Col md={6} className="secondaryMuscleGroup">
              <Form.Group controlId="secondaryMuscleGroup">
                <Form.Label>
                  {trans("Exercise.SecondaryMuscleGroup")}
                </Form.Label>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="Select Secondary Muscle Groups"
                  value={formData.secondaryMuscleGroup}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      secondaryMuscleGroup: value,
                    }))
                  }
                  disabled={formData.disabledFields}
                >
                  {muscleGroups?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group controlId="instructions">
            <Form.Label>{trans("Exercise.Instruction")}</Form.Label>
            <textarea
              rows={4}
              className="form-control"
              value={formData.instructions}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  instructions: e.target.value,
                }))
              }
              disabled={formData.disabledFields}
            />
          </Form.Group>

          {/* Image & Video Upload */}
          <Row>
            <Col>
              <Form.Label>Exercise Photo (Max: 10MB)</Form.Label>
              <div className="file-upload-box1">
                <Upload.Dragger
                  customRequest={({ onSuccess }) => onSuccess("ok")}
                  onChange={(info) => handleFileChange(info, "photo")}
                  showUploadList={false}
                  accept="image/*"
                  disabled={formData.disabledFields}
                >
                  <p className="ant-upload-text">
                    {trans("Common.chooseFile")}
                  </p>
                  <p className="ant-upload-hint">
                    {trans("Common.dragYourFile")}
                  </p>
                </Upload.Dragger>
              </div>
              {errors.photo && (
                <div className="text-danger mt-1">{errors.photo}</div>
              )}
              {formData.photoName && (
                <div className="file-info">
                  <span>{formData.photoName}</span>
                  {!formData.disabledFields ? (
                    <DeleteOutline
                      className="delete-icon-exercise"
                      onClick={() => handleDeleteFile("photo")}
                      disabled={formData.disabledFields}
                    />
                  ) : null}
                </div>
              )}
            </Col>

            <Col>
              <Form.Label>Exercise Video (Max: 500MB)</Form.Label>
              <div className="file-upload-box1">
                <Upload.Dragger
                  customRequest={({ onSuccess }) => onSuccess("ok")}
                  onChange={(info) => handleFileChange(info, "video")}
                  showUploadList={false}
                  accept="video/*"
                  disabled={formData.disabledFields}
                >
                  <p className="ant-upload-text">
                    {trans("Common.chooseFile")}
                  </p>
                  <p className="ant-upload-hint">
                    {trans("Common.dragYourFile")}
                  </p>
                </Upload.Dragger>
              </div>
              {errors.video && (
                <div className="text-danger mt-1">{errors.video}</div>
              )}
              {formData.videoName && (
                <div className="file-info">
                  <span>{formData.videoName}</span>
                  {!formData.disabledFields ? (
                    <DeleteOutline
                      className="delete-icon-exercise"
                      onClick={() => handleDeleteFile("video")}
                      disabled={formData.disabledFields}
                    />
                  ) : null}
                </div>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default Exercises;
